import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    LinearProgress, 
    Alert, 
    Chip, 
    Collapse, 
    FormGroup, 
    FormControlLabel, 
    Checkbox,
    Button
} from '@mui/material';
import { Download, FileDownload, Settings } from '@mui/icons-material';
import { ExtractField, EXTRACT_FIELDS_CONFIG, DEFAULT_EXTRACT_FIELDS, ESSENTIAL_EXTRACT_FIELDS } from '../../../../types/spendingDemand.types';

export interface ExtractViewProps {
    isExtracting: boolean;
    extractStatus: {
        status: 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND' | null;
        total: number;
        actual: number;
    };
    extractProcessId: string | null;
    selectedExtractFields: ExtractField[];
    onStartExtract: () => void;
    onDownloadExtract: () => void;
    onFieldChange: (field: ExtractField, checked: boolean) => void;
    isLoading: boolean;
    selectedCount: number;
}

export default function ExtractView({
    isExtracting,
    extractStatus,
    extractProcessId,
    selectedExtractFields,
    onStartExtract,
    onDownloadExtract,
    onFieldChange,
    isLoading,
    selectedCount
}: ExtractViewProps) {
    const [showFieldSelector, setShowFieldSelector] = useState(false);

    const toggleFieldSelector = () => {
        setShowFieldSelector(!showFieldSelector);
    };

    const resetToDefaultFields = () => {
        DEFAULT_EXTRACT_FIELDS.forEach(field => {
            if (!selectedExtractFields.includes(field)) {
                onFieldChange(field, true);
            }
        });
        // Retirer les champs qui ne sont pas dans DEFAULT_EXTRACT_FIELDS
        selectedExtractFields.forEach(field => {
            if (!DEFAULT_EXTRACT_FIELDS.includes(field) && !ESSENTIAL_EXTRACT_FIELDS.includes(field)) {
                onFieldChange(field, false);
            }
        });
    };

    const selectAllFields = () => {
        Object.keys(EXTRACT_FIELDS_CONFIG).forEach(field => {
            if (!selectedExtractFields.includes(field as ExtractField)) {
                onFieldChange(field as ExtractField, true);
            }
        });
    };

    const getStatusColor = (status: string | null) => {
        switch (status) {
            case 'PENDING':
                return 'warning';
            case 'COMPLETED':
                return 'success';
            case 'ERROR':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusText = (status: string | null) => {
        switch (status) {
            case 'PENDING':
                return 'En cours...';
            case 'COMPLETED':
                return 'Terminé';
            case 'ERROR':
                return 'Erreur';
            case 'NOT_FOUND':
                return 'Introuvable';
            default:
                return 'Prêt';
        }
    };

    const getProgressPercentage = () => {
        if (extractStatus.total === 0) return 0;
        return Math.round((extractStatus.actual / extractStatus.total) * 100);
    };

    if (selectedCount === 0) {
        return null;
    }

    return (
        <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 1.5, 
            backgroundColor: '#fafafa',
            mb: 2
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileDownload sx={{ fontSize: 18, color: '#666' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#333' }}>
                        Extraire les demandes sélectionnées
                    </Typography>
                </Box>
                {(extractStatus.status || isExtracting) && (
                    <Chip 
                        label={isExtracting && !extractStatus.status ? '...' : 
                               extractStatus.status === 'PENDING' ? '...' :
                               extractStatus.status === 'COMPLETED' ? '✓' :
                               extractStatus.status === 'ERROR' ? '!' : '...'} 
                        color={isExtracting && !extractStatus.status ? 'warning' : getStatusColor(extractStatus.status) as any}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                )}
            </Box>

            {/* Informations */}
            <Typography variant="body2" sx={{ color: '#666', mb: 1.5 }}>
                {selectedCount} {selectedCount > 1 ? 'demandes' : 'demande'} sélectionnée{selectedCount > 1 ? 's' : ''}
            </Typography>

            {/* Bouton de configuration des champs */}
            {!isExtracting && extractStatus.status !== 'COMPLETED' && (
                <Button
                    onClick={toggleFieldSelector}
                    size="small"
                    variant="outlined"
                    startIcon={<Settings sx={{ fontSize: 14 }} />}
                    sx={{ 
                        mb: 1.5,
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        borderColor: '#ccc',
                        color: '#666',
                        '&:hover': {
                            borderColor: '#999',
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                >
                    {showFieldSelector ? 'Masquer champs' : 'Configurer champs'}
                </Button>
            )}

            {/* Sélecteur de champs */}
            <Collapse in={showFieldSelector}>
                <Box sx={{ 
                    mb: 1.5, 
                    p: 1.5, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 1, 
                    border: '1px solid #e0e0e0',
                    maxHeight: '200px',
                    overflowY: 'auto'
                }}>
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 1, display: 'block' }}>
                        Champs sélectionnés ({selectedExtractFields.length})
                    </Typography>
                    
                    {/* Boutons d'actions rapides */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Button
                            onClick={resetToDefaultFields}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                height: '24px',
                                minWidth: '60px'
                            }}
                        >
                            Défaut
                        </Button>
                        <Button
                            onClick={selectAllFields}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                height: '24px',
                                minWidth: '60px'
                            }}
                        >
                            Tout
                        </Button>
                    </Box>

                    {/* Champs par catégorie */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                        {Object.entries(
                            Object.keys(EXTRACT_FIELDS_CONFIG).reduce((acc, field) => {
                                const category = EXTRACT_FIELDS_CONFIG[field as ExtractField].category;
                                if (!acc[category]) acc[category] = [];
                                acc[category].push(field as ExtractField);
                                return acc;
                            }, {} as Record<string, ExtractField[]>)
                        ).map(([category, fields]) => (
                            <Box key={category} sx={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px' }}>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5, display: 'block' }}>
                                    {category} ({fields.filter(f => selectedExtractFields.includes(f)).length}/{fields.length})
                                </Typography>
                                <FormGroup>
                                    {fields.map(field => (
                                        <FormControlLabel
                                            key={field}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedExtractFields.includes(field)}
                                                    onChange={(e) => onFieldChange(field, e.target.checked)}
                                                    disabled={ESSENTIAL_EXTRACT_FIELDS.includes(field)}
                                                    sx={{ 
                                                        '& .MuiSvgIcon-root': { fontSize: 14 },
                                                        py: 0
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                    {EXTRACT_FIELDS_CONFIG[field].label}
                                                    {ESSENTIAL_EXTRACT_FIELDS.includes(field) && ' *'}
                                                </Typography>
                                            }
                                            sx={{ mb: 0, py: 0 }}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        ))}
                    </Box>
                    
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mt: 1 }}>
                        * Champs obligatoires
                    </Typography>
                </Box>
            </Collapse>

            {/* Affichage du statut */}
            {(extractStatus.status || isExtracting) && (
                <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {extractStatus.status === 'PENDING' || (isExtracting && !extractStatus.status) 
                                ? `${extractStatus.actual || 0} / ${extractStatus.total || 0}` 
                                : getStatusText(extractStatus.status)
                            }
                        </Typography>
                    </Box>

                    {/* Barre de progression */}
                    {(extractStatus.status === 'PENDING' || (isExtracting && !extractStatus.status)) && (
                        <LinearProgress 
                            variant={extractStatus.total > 0 ? "determinate" : "indeterminate"}
                            value={extractStatus.total > 0 ? getProgressPercentage() : undefined}
                            sx={{ mb: 1, height: 6, borderRadius: 3 }}
                        />
                    )}

                    {/* Messages */}
                    {extractStatus.status === 'ERROR' && (
                        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
                            Erreur lors de l'extraction
                        </Alert>
                    )}

                    {(isExtracting && !extractStatus.status) && (
                        <Alert severity="info" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
                            Initialisation...
                        </Alert>
                    )}
                </Box>
            )}

            {/* Boutons d'action */}
            <Box sx={{ display: 'flex', gap: 1 }}>
                {!isExtracting && extractStatus.status !== 'COMPLETED' && (
                    <Button
                        onClick={onStartExtract}
                        disabled={isLoading || selectedCount === 0}
                        variant="contained"
                        startIcon={<FileDownload />}
                        sx={{
                            flex: 1,
                            textTransform: 'none',
                            backgroundColor: '#757575',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#616161'
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#999'
                            }
                        }}
                    >
                        {isLoading ? 'Démarrage...' : 'Extraire'}
                    </Button>
                )}

                {extractStatus.status === 'COMPLETED' && extractProcessId && (
                    <Button
                        onClick={onDownloadExtract}
                        disabled={isLoading}
                        variant="contained"
                        startIcon={<Download />}
                        sx={{
                            flex: 1,
                            textTransform: 'none',
                            backgroundColor: '#4caf50',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#45a049'
                            }
                        }}
                    >
                        {isLoading ? 'Téléchargement...' : 'Télécharger'}
                    </Button>
                )}
            </Box>
        </Box>
    );
}

